import React, { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useSetupPodioWebhookMutation } from "@/store/rest_api";
import toast from "react-hot-toast";

interface WebhookSetupData {
  webhookUrl: string;
  secret: string;
}

export default function PodioWebhook() {
  const [setupWebhook, { isLoading: isSettingUp }] = useSetupPodioWebhookMutation();
  const [webhookData, setWebhookData] = useState<WebhookSetupData | null>(null);

  const handleSetup = async () => {
    try {
      const result = await setupWebhook().unwrap();
      setWebhookData(result as WebhookSetupData);
    } catch {
      toast.error("Failed to set up webhook");
    }
  };

  const handleCopy = () => {
    if (webhookData) {
      navigator.clipboard.writeText(webhookData.webhookUrl);
      toast.success("Webhook URL copied to clipboard");
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Real-time Webhook</CardTitle>
          <CardDescription>
            Register this URL on your Podio Contacts app to get real-time contact sync.
            The URL carries a secret token, so only Podio can post to it. Without this,
            contacts are only synced when you click "Sync Now".
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!webhookData ? (
            <Button onClick={handleSetup} disabled={isSettingUp}>
              {isSettingUp ? "Generating..." : "Generate Webhook URL"}
            </Button>
          ) : (
            <>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">Webhook URL:</span>
                <code className="text-xs bg-muted px-2 py-1 rounded break-all">{webhookData.webhookUrl}</code>
              </div>
              <Button variant="outline" onClick={handleCopy}>
                Copy Webhook URL
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      {webhookData && (
        <Card>
          <CardHeader>
            <CardTitle>Installation Instructions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <ol className="list-decimal list-inside space-y-2 text-muted-foreground">
              <li>Copy the webhook URL using the button above</li>
              <li>In Podio, open your <strong>Contacts app</strong> and go to <strong>Modify app → Developer</strong></li>
              <li>Under <strong>Webhooks</strong>, paste the URL and create hooks for <strong>item.create</strong>, <strong>item.update</strong>, and <strong>item.delete</strong></li>
              <li>Podio sends a verification request automatically, which the URL answers to activate the hook</li>
            </ol>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
